import React, { useCallback } from 'react';

import { useExport, ExportFormats } from '@qualio/export-components';
import {
  QButton,
  QHeading,
  QFlex,
  QTabs,
  QSpacer,
  QBox,
  QMenuButton,
  QMenuItem,
  useCurrentUser,
} from '@qualio/ui-components';
import { exportApi } from 'api';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { routes } from 'utils';

import { POLICY_TABS, activePolicyRouteId } from '../constants/const';

export const PolicyHeader: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { supplierExport } = useFlags();
  const { companyId } = useCurrentUser();

  // Translate tabs at runtime
  const translatedPolicyTabs = POLICY_TABS.map((tab) => ({
    ...tab,
    title: t(`supplierPolicy.tabs.${tab.id === 'documents' ? 'requiredDocuments' : 'audits'}`),
  }));

  const { trigger: handleTriggerExport, isLoading: isExportLoading } = useExport(exportApi, {
    toastTitle: t('toastMessages.export.configExportToast'),
    'data-cy': 'supplier-policy-export',
  });

  const handleEdit = useCallback(() => {
    navigate(`/${routes.policyConfiguration.root}/${routes.policyConfiguration.supplierTypes}`);
  }, [navigate]);

  const handleExport = useCallback(
    () =>
      handleTriggerExport({
        exports: [
          {
            format: ExportFormats.fetch_payload,
            data: {
              export_payload: `${process.env.REACT_APP_API_BASE_URL}/company/${companyId}/sqm/config/export-payload`,
              resource_description: `Supplier configuration export ${companyId}`,
            },
          },
        ],
        metadata: {
          domain: 'supplier_configuration',
          resource_name: 'Supplier configurations export',
        },
      }),
    [companyId, handleTriggerExport],
  );

  return (
    <QBox my={4}>
      <QFlex direction="row" justifyContent="space-between">
        <QHeading size={'lg'}>{t('supplierPolicy.heading')}</QHeading>
        {supplierExport ? (
          <QMenuButton
            buttonLabel={t('supplierPolicy.buttons.configuration')}
            variant="outline"
            isDisabled={isExportLoading}
          >
            <QMenuItem onClick={handleEdit}>{t('supplierPolicy.buttons.edit')}</QMenuItem>
            <QMenuItem onClick={handleExport} isDisabled={isExportLoading}>
              {t('supplierPolicy.buttons.export')}
            </QMenuItem>
          </QMenuButton>
        ) : (
          <QButton
            variant="outline"
            onClick={() => navigate(`/${routes.policyConfiguration.root}/${routes.policyConfiguration.supplierTypes}`)}
          >
            {t('supplierPolicy.buttons.configuration')}
          </QButton>
        )}
      </QFlex>
      <QFlex w="100%" justifyContent="space-between">
        <QSpacer />
        <QTabs
          activeTabId={activePolicyRouteId(pathname)}
          tabs={translatedPolicyTabs}
          onClick={(tabItem) => navigate(`/${routes.policy.root}/${tabItem.id}`)}
        />
        <QSpacer />
      </QFlex>
    </QBox>
  );
};
